import React from 'react';
import { TruncatedText } from '@dynamika-shared/ui/TruncatedText/TruncatedText';
import { ColumnType } from '@dynamika-shared/ui/Table/Table.types';
import { formatUtcDateTime } from '@dynamika-shared/utils/date';
import { StatusPill } from '@dynamika-shared/ui/StatusPill/StatusPill';
import { Button } from '@dynamika-shared/ui/Button/Button';
import ChevronIcon from '@dynamika-shared/ui/icons/Chevron';
import { QcStatusEnum, StudyStatusEnum, UserStatusEnum, PatientUploadStatusEnum } from '@dynamika-shared/types/status';
import { ReadingStatus, Reading } from '@dynamika-shared/types/readings.types';
import { TaskDefinitionExecutionNamedStatus } from '@dynamika-shared/types/qcTaskDefinitions';
import { ExecutionStatus } from '@dynamika-shared/types/executions';
import { formatStatusLabel } from '@utils/statusLabel';

type SupportedStatus = ExecutionStatus | QcStatusEnum | TaskDefinitionExecutionNamedStatus | PatientUploadStatusEnum | StudyStatusEnum | UserStatusEnum | ReadingStatus;

const loading = () => (
  <div className='h-2 w-10 animate-pulse rounded-md bg-slate-300' />
);

export const tasksTableConfig = (): ColumnType<Reading>[] => [
  {
    name: 'readingCode',
    thClassName: 'w-[10%]',
    header() {
      return 'Reading ID';
    },
    hasSorting: true,
    loading,
    getValue(row) {
      return <TruncatedText text={row.readingCode} truncate tooltip />;
    },
    getCsvValue(row) {
      return row.readingCode ?? '';
    },
  },
  {
    name: 'patientCode',
    thClassName: 'w-[10%]',
    header() {
      return 'Subject ID';
    },
    hasSorting: true,
    loading,
    getValue(row) {
      return <TruncatedText text={row.patientCode} truncate tooltip />;
    },
    getCsvValue(row) {
      return row.patientCode ?? '';
    },
  },
  {
    name: 'visitName',
    thClassName: 'w-[10%]',
    header() {
      return 'Visit Name';
    },
    hasSorting: true,
    loading,
    getValue(row) {
      return <TruncatedText text={row.visitName} truncate tooltip />;
    },
    getCsvValue(row) {
      return row.visitName ?? '';
    },
  },
  {
    name: 'createdDate',
    thClassName: 'w-[15%]',
    header() {
      return 'Creation Date (UTC)';
    },
    hasSorting: true,
    loading,
    getValue(row) {
      return (
        <TruncatedText
          text={formatUtcDateTime(row.createdDate)}
          truncate
          tooltip
        />
      );
    },
    getCsvValue(row) {
      return row.createdDate
        ? formatUtcDateTime(row.createdDate)
        : '-';
    },
  },
  {
    name: 'scoringFormName',
    header() {
      return 'Reading Criteria';
    },
    thClassName: 'w-[20%]',
    loading,
    getValue(row) {
      return <TruncatedText text={row?.scoringFormName?.replace(/_/g,' ')} truncate tooltip />;
    },
    getCsvValue(row) {
      return row?.scoringFormName?.replace(/_/g, ' ') ?? '';
    },
  },
  {
    name: 'assigneeName',
    header() {
      return 'Reader';
    },
    thClassName: 'w-[15%]',
    loading,
    getValue(row) {
      return <TruncatedText text={row.assigneeName} truncate tooltip />;
    },
    getCsvValue(row) {
      return row.assigneeName ?? '';
    },
  },
  {
    name: 'status',
    thClassName: 'w-[10%]',
    header() {
      return 'Status';
    },
    hasSorting: true,
    loading,
    getValue(row) {
      return row.status ? (
        <StatusPill
          status={row.status as SupportedStatus}
          textSeparator='_'
          textCombiner=' '
        />
      ) : (
        ''
      );
    },
    getCsvValue(row) {
      return row.status ? formatStatusLabel(row.status, '_', ' ') : '';
    },
  },
  {
    name: 'startReadingTime',
    thClassName: 'w-[15%]',
    header() {
      return 'Started Date (UTC)';
    },
    hasSorting: true,
    loading,
    getValue(row) {
      return row.startReadingTime ? (
        <TruncatedText
          text={formatUtcDateTime(row.startReadingTime)}
          truncate
          tooltip
        />
      ) : (
        '-'
      );
    },
    getCsvValue(row) {
      return row.startReadingTime
        ? formatUtcDateTime(row.startReadingTime)
        : '-';
    },
  },
  {
    name: 'finishReadingTime',
    thClassName: 'w-[15%]',
    header() {
      return 'Completion Date (UTC)';
    },
    hasSorting: true,
    loading,
    getValue(row) {
      return row.finishReadingTime ? (
        <TruncatedText
          text={formatUtcDateTime(row.finishReadingTime)}
          truncate
          tooltip
        />
      ) : (
        '-'
      );
    },
    getCsvValue(row) {
      return row.finishReadingTime
        ? formatUtcDateTime(row.finishReadingTime)
        : '-';
    },
  },
  {
    name: 'action',
    thClassName: 'w-[10%]',
    header() {
      return 'Action';
    },
    getValue() {
      return (
        <Button
          type='text'
          onClick={() => null}
          className='flex h-4 !min-h-fit w-4 cursor-pointer items-center justify-center !p-0'
        >
          <ChevronIcon dir='right' className='h-2 w-2 text-pastelBlue' />
        </Button>
      );
    },
    loading,
  },
];
